import { CanvaSettings } from '../types/Canva';
import { GenerateImageRequest, GenerateImageResponse } from '../types/CanvaGenerateInterface';
import * as canvasClient from '../utils/apiClients/canvasClient';
import imageGenerationClient from '../utils/apiClients/imagesClient';
import { isAxiosError } from '../utils/axios';
import { COMPONENT_STATE_DISPLAY_MODAL } from '../pages/Canvas/Component/CanvasModalContainer';
import { BACKGROUND_REFERENCE_IMAGE } from '../pages/Canvas/Component/ReplaceBackgroundModal';

interface ErrorResponse {
  [x: string]: any;
  message: string;
}

// Helper function to check if error is related to invalid dimensions
function isDimensionError(error: any): boolean {
  if (!error) return false;
  
  const errorMessage = 
    error?.response?.data?.errors?.[0]?.message ||
    error?.response?.data?.message ||
    error?.message ||
    '';
  
  const errorString = JSON.stringify(error?.response?.data || error || '').toLowerCase();
  
  // Check for dimension-related keywords
  const dimensionKeywords = [
    'image_size',
    'width',
    'height',
    'dimension',
    'invalid parameter',
    'invalid_request',
    'custom_image_size'
  ];
  
  return dimensionKeywords.some(keyword => 
    errorString.includes(keyword) || errorMessage.toLowerCase().includes(keyword)
  );
}

export default class CanvasApi {
  private randomNumber: number;

  private setUpdateCanvas: (value: boolean) => void;

  private canvas: any | null;

  private settings: CanvaSettings | null;

  private modalControl: { [key: string]: { state: boolean; control: (value: any) => void } } = {};

  private componentState: { [key: string]: { value: any; update: (value: any) => void } } = {};

  constructor(setUpdateCanvas: (value: boolean) => void) {
    this.randomNumber = Math.random();
    this.setUpdateCanvas = setUpdateCanvas;
  }

  registerCanvas(canvas: any) {
    this.canvas = canvas;
  }

  setCanvasSettings(canvaSettings: CanvaSettings) {
    this.settings = canvaSettings;
  }

  async generateBasicImageFromPrompt() {
    const prompt = this.canvas?.getPrompt();
    if (!prompt || prompt.trim() == '') return this.canvas?.showSnackbar("Prompt can't be blank!", 'warning');

    this.executeImageGeneration(async request => request, this.requestGenerateImage);
  }

  async executeImageGeneration(
    requestBuilder: (request: GenerateImageRequest) => Promise<GenerateImageRequest>,
    requestDispatcher: (client: canvasClient.CanvasClient, request: GenerateImageRequest) => Promise<GenerateImageResponse>
  ) {
    try {
      this.canvas?.scrollToTop();

      this.canvas?.setIsGenerating(true);

      const request: GenerateImageRequest = await requestBuilder(this.buildImageRequest());

      const response: GenerateImageResponse = await requestDispatcher(canvasClient.client, request);

      this.handleNewCanvasImageResponse(response);
    } catch (error: unknown) {
      // Handle validation errors (show as warning)
      if (error instanceof Error && (error as any).isValidationError) {
        const snackbarType = (error as any).isWarning ? 'warning' : 'error';
        this.canvas?.showSnackbar(error.message, snackbarType);
        return;
      }
      
      if (isAxiosError(error)) {
        const errorResponse = error.response?.data as ErrorResponse;
        const reason = errorResponse?.errors?.[0]?.reason;
        const current = errorResponse?.errors?.[0]?.currentCredits;
        const required = errorResponse?.errors?.[0]?.required;

        // Check if it's a dimension-related error
        if (isDimensionError(error)) {
          this.canvas?.showSnackbar('Please enter valid width and height.', 'warning');
          return;
        }

        const errorMessage =
          reason === 'Not enough credits for the operation' && current !== undefined && required !== undefined
            ? `You do not have enough credits. Required: ${required}, Available: ${current}.`
            : (errorResponse?.message ?? 'Error in generating image. Please try again.');

        console.error('Error in generating image:', errorResponse?.message ?? error.message);
        this.canvas?.showSnackbar(errorMessage ?? 'Error in generating image. Please try again.', 'error');
      } else {
        // Check if it's a dimension-related error even if not axios error
        if (isDimensionError(error)) {
          this.canvas?.showSnackbar('Please enter valid width and height.', 'warning');
          return;
        }
        
        console.error('Unexpected error:', error);
        this.canvas?.showSnackbar('An unexpected error occurred. Please try again.', 'error');
      }
    } finally {
      this.canvas?.setIsGenerating(false);
    }
  }

  async generateImageRemoveBackgroundFromSubject() {
    this.canvas?.scrollToTop();

    if (!this.settings?.canvas_subject) {
      console.log('No subject image found to remove an image from');
      this.canvas?.showSnackbar('No image selected to remove background from.  Generate an image first.', 'warning');
      return;
    }

    try {
      this.canvas?.setIsGenerating(true);

      const request = this.buildImageRequest();

      const { data }: { data: GenerateImageResponse } = await canvasClient.client.upscaleSubject(request);
      this.handleNewCanvasImageResponse(data);
    } finally {
      this.canvas?.setIsGenerating(false);
    }
  }

  async requestGenerateImage(client: canvasClient.CanvasClient, request: GenerateImageRequest): Promise<GenerateImageResponse> {
    if (request.pose_and_depth) {
      const { data }: { data: GenerateImageResponse } = await client.generatePoseAndDepth(request);
      return data;
    } else {
      const reqUrl = '/images';
      const { data }: { data: GenerateImageResponse } = await imageGenerationClient.post(reqUrl, request);
      return data;
    }
  }

  async createMaskFromPrompt(client: canvasClient.CanvasClient, request: GenerateImageRequest): Promise<GenerateImageResponse> {
    const { data }: { data: GenerateImageResponse } = await client.createMaskFromPrompt(request);
    return data; // Add this line to return the data
  }

  async createAutoMask(client: canvasClient.CanvasClient, request: GenerateImageRequest): Promise<GenerateImageRequest> {
    const { data }: { data: GenerateImageRequest } = await client.createAutoMasks(request);
    return data;
  }

  async eraseObjects(client: canvasClient.CanvasClient, request: GenerateImageRequest): Promise<GenerateImageRequest> {
    const { data }: { data: GenerateImageRequest } = await client.createAutoMasks(request);
    return data;
  }

  async InpaintObjects(client: canvasClient.CanvasClient, request: GenerateImageRequest): Promise<GenerateImageRequest> {
    const { data }: { data: GenerateImageRequest } = await client.createAutoMasks(request);
    return data;
  }

  async outpaintSubject(client: canvasClient.CanvasClient, request: GenerateImageRequest): Promise<GenerateImageRequest> {
    const { data }: { data: GenerateImageRequest } = await client.outpaintSubject(request);
    return data;
  }

  registerComponentState(name: any, val: any, updateState: (value: any) => void) {
    this.componentState[name] = { value: val, update: updateState };
  }

  updateComponentState(name: any, val: any) {
    this.componentState[name]?.update(val);
  }

  getComponentState(name: any) {
    return this.componentState[name]?.value;
  }

  registerModalControl(modalType: any, state: boolean, modalControl: (value: any) => void) {
    this.modalControl[modalType] = { state: state, control: modalControl };
  }

  displayModal(modalType: any) {
    this.modalControl[modalType]?.control(true);
    this.updateComponentState(COMPONENT_STATE_DISPLAY_MODAL, true);
  }

  hideModal(modalType: any) {
    this.updateComponentState(COMPONENT_STATE_DISPLAY_MODAL, false);
    this.modalControl[modalType]?.control(false);
  }

  hideAllModals() {
    Object.keys(this.modalControl).forEach(key => {
      if (this.modalControl[key]?.state) {
        this.hideModal(key);
      }
    });
  }

  handleNewCanvasImageResponse(response: GenerateImageResponse) {
    if (response) {
      const firstImage = (response.images || [])[0] || undefined;
      if (firstImage) {
        this.canvas?.setCanvasSettings(settings => ({
          ...settings,
          canvas_subject: firstImage.id
        }));
      }
      this.canvas?.setImagesResponse(imgs => [response, ...imgs]);
    }
  }

  helloWorld() {
    console.log('Hello World: ', this.randomNumber);
  }

  buildImageRequest(): GenerateImageRequest {
    // Check if this is a LoRA model using modelType from API response
    // This is the primary and most reliable way to detect LoRA models
    const isLoRAModel = this.settings?.modelType === 'text-to-image-lora';

    // Check if this is an edit model using modelType from API response
    // This is the primary and most reliable way to detect edit models
    let isEditModel = this.settings?.modelType === 'image-editing';
    
    // Fallback: If modelType is not set but we have image_urls in settings, it's likely an edit model
    // This handles cases where modelType might not be set correctly
    if (!isEditModel && this.settings?.image_urls && this.settings.image_urls.length > 0) {
      console.warn('ModelType not set but image_urls found - treating as edit model');
      isEditModel = true;
    }
    

    const aspectRatio = this.settings?.aspectRatio;
    const isCustomAspectRatio = aspectRatio && aspectRatio.toLowerCase() === 'custom';
    
    // Parse dimensions
    const dims = this.settings?.image_dimensions.split('x') || ['1024', '768'];
    const width = parseInt(dims[0]) || 1024;
    const height = parseInt(dims[1]) || 768;

    // Validate custom dimensions (minimum 64x64, maximum 4096x4096)
    if (isCustomAspectRatio) {
      const MIN_DIMENSION = 64;
      const MAX_DIMENSION = 4096;
      
      if (width < MIN_DIMENSION || height < MIN_DIMENSION) {
        const error = new Error(`Custom dimensions are too small. Minimum size is ${MIN_DIMENSION}x${MIN_DIMENSION}. Please enter valid dimensions.`);
        (error as any).isValidationError = true;
        (error as any).isWarning = true;
        throw error;
      }
      
      if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
        const error = new Error(`Custom dimensions are too large. Maximum size is ${MAX_DIMENSION}x${MAX_DIMENSION}. Please enter valid dimensions.`);
        (error as any).isValidationError = true;
        (error as any).isWarning = true;
        throw error;
      }
    }

    // For edit models, ensure image_urls is set
    // Ensure imageUrls is always an array
    let imageUrls: string[] = Array.isArray(this.settings?.image_urls) 
      ? this.settings.image_urls 
      : (this.settings?.image_urls ? [this.settings.image_urls] : []);
    
    
    if (isEditModel) {
      // If image_urls is not set in settings, try to get it from the canvas
      if (!imageUrls || imageUrls.length === 0) {
        const uploadedImageUrl = this.canvas?.getUploadedImage?.();
        
        if (uploadedImageUrl) {
          // Ensure it's an array
          imageUrls = Array.isArray(uploadedImageUrl) ? uploadedImageUrl : [uploadedImageUrl];
         
        } else {
          // If still no image URL, show an error
          
          const error = new Error('Please upload or select an image to edit.');
          (error as any).isValidationError = true;
          (error as any).isWarning = true;
          throw error;
        }
      } else {
        console.log('Using image_urls from settings:', imageUrls);
      }
      // Final check - ensure imageUrls is set and is an array
      if (!Array.isArray(imageUrls) || imageUrls.length === 0) {

        const error = new Error('Please upload or select an image to edit.');
        (error as any).isValidationError = true;
        (error as any).isWarning = true;
        throw error;
      }
    }
    

    let body: GenerateImageRequest;

    if (isEditModel) {
      // Special payload format for ALL image editing models
      body = {
        prompt: this.canvas?.getPrompt() || '',
        model_id: this.settings?.modelId || '',
        width: width,
        height: height,
        num_images: this.settings?.number_of_images || 1,
        image_format: this.settings?.imageFormat || 'jpeg',
        operation: 'generate',
        model_parameters: {
          image_urls: imageUrls,
          aspect_ratio: aspectRatio || '16:9',
          num_images: this.settings?.number_of_images || 1
        },
        image_categories: this.settings?.style 
          ? ['art_style'] 
          : this.settings?.project 
            ? ['persona'] 
            : ['vanilla']
      };

      if (this.settings?.style) {
        body.artStyleId = this.settings.style.id;
      } else if (this.settings?.project) {
        body.artStyleId = this.settings.project.id;
      }

      if (this.settings?.project) {
        body.project_id = this.settings.project.projectID;
      }

      // CRITICAL: For edit models, image_urls MUST be present in the request
      if (!body.model_parameters.image_urls || body.model_parameters.image_urls.length === 0) {
        const error = new Error('Image URL is required for editing models. Please upload an image.');
        (error as any).isValidationError = true;
        (error as any).isWarning = true;
        throw error;
      }
    } else {
      // Standard payload format for text-to-image models
      body = {
        prompt: this.canvas?.getPrompt(),
        numImages: this.settings?.number_of_images || 1,

        // For LoRA models, send modelId to identify the model (especially important for vanilla images)
        // For non-LoRA models, send modelId if it exists
        ...(this.settings?.modelId ? { modelId: this.settings.modelId } : {}),
        
        // For LoRA models, also send model name so backend knows which base model was used
        ...(isLoRAModel && this.settings?.modelName ? { modelName: this.settings.modelName } : {}),

        ...(this.settings?.style ? { artStyleId: this.settings?.style?.id } : {}),
        ...(this.settings?.project ? { artStyleId: this.settings?.project?.id } : {}),
        ...(this.settings?.project ? { project_id: this.settings?.project?.projectID } : {}),
        ...(this.settings?.pose_and_depth ? { pose_and_depth: this.settings?.pose_and_depth } : {}),
        ...(this.settings?.canvas_subject ? { subject: this.settings?.canvas_subject } : {}),

        // Add dynamic parameters based on model
        // Resolution for Google Nano Banana Pro
        ...(this.settings?.resolution ? { resolution: this.settings.resolution } : {}),
        
        // Image URLs for editing models - REQUIRED for edit models
        // Always include image_urls if it exists (for any model)
        // For edit models, we've already validated it exists above
        // CRITICAL: Always send as array format [url1, url2, url3]
        ...(Array.isArray(imageUrls) && imageUrls.length > 0 ? { image_urls: imageUrls } : {}),
      };

      // For LoRA models, structure parameters according to API spec
      if (isLoRAModel) {
        // Initialize model_parameters for LoRA models
        body.model_parameters = body.model_parameters || {};
        
        if (this.settings?.style?.id) {
          body.model_parameters.art_style_id = this.settings.style.id;
        } else if (this.settings?.project?.id) {
          body.model_parameters.art_style_id = this.settings.project.id;
        }
        
        // Send num_images in model_parameters for LoRA models
        body.model_parameters.num_images = this.settings?.number_of_images || 1;
      }

      // Handle aspect ratio and dimensions for ALL models
      // Always send aspect_ratio at top level (never send width/height at top level)
      // Explicitly remove width/height to ensure they're never sent at top level
      delete body.width;
      delete body.height;
      
      if (aspectRatio) {
        // Send aspect_ratio at top level for all models
        body.aspect_ratio = aspectRatio;
        
        // If Custom, include custom_image_size in model_parameters
        if (isCustomAspectRatio) {
          body.model_parameters = {
            ...(body.model_parameters || {}),
            custom_image_size: {
              width: width,
              height: height
            }
          };
        }
        
        // For LoRA models, also send image_size in model_parameters (mapped from feAspectRatioMapping)
        if (isLoRAModel && this.settings?.feAspectRatioMapping) {
          const mappedImageSize = this.settings.feAspectRatioMapping[aspectRatio];
          if (mappedImageSize) {
            body.model_parameters = {
              ...(body.model_parameters || {}),
              image_size: mappedImageSize
            };
          }
        }
      } else {
        // If aspectRatio is not set, we still don't send width/height
        // Log a warning to help debug
        console.warn('aspectRatio is not set in canvaSettings. Please ensure aspect ratio is selected in the UI.');
      }
      // Never send width/height at top level for any model

      // Set image categories
      if (this.settings?.style) {
        body.imageCategories  = ['art_style'];
      } else if (this.settings?.project) {
        body.imageCategories  = ['persona'];
      } else {
        body.imageCategories  = ['vanilla'];
      }
    }

    return body;
  }

  async addBackgroundImageAsReferenceImageToRequest(request: GenerateImageRequest) {
    const backgroundReferenceImage = this.getComponentState(BACKGROUND_REFERENCE_IMAGE);
    const refImageData = await this.toBase64(backgroundReferenceImage);
    return {
      ...request,
      reference_image: refImageData
    };
  }

  buildVideoRequest(): any {
    const body: any = {
      prompt: this.canvas?.getPrompt(),
      modelId: this.settings?.videoModelId || null,
    };

    // Helper function to check if a parameter is supported by the model
    const hasParameter = (paramName: string): boolean => {
      if (!this.settings?.videoModelParameters || !Array.isArray(this.settings.videoModelParameters)) {
        return false;
      }
      return this.settings.videoModelParameters.some(p => p.name === paramName);
    };

    // Build modelParameters object with video-specific parameters
    // Only include parameters that the model actually supports
    const modelParameters: any = {};

    // Duration parameter
    if (hasParameter('duration') && this.settings?.videoDuration !== null && this.settings?.videoDuration !== undefined) {
      modelParameters.duration = this.settings.videoDuration;
    }

    // Aspect ratio parameter
    if (hasParameter('aspect_ratio') && this.settings?.videoAspectRatio) {
      modelParameters.aspect_ratio = this.settings.videoAspectRatio;
    }

    // Resolution parameter
    if (hasParameter('resolution') && this.settings?.videoResolution) {
      modelParameters.resolution = this.settings.videoResolution;
    }

    // Generate audio parameter
    if (hasParameter('generate_audio') && this.settings?.videoGenerateAudio !== null && this.settings?.videoGenerateAudio !== undefined) {
      modelParameters.generate_audio = this.settings.videoGenerateAudio;
    }

    // BGM Music parameter (check for variations: bgm_music, bgmMusic, background_music, bgm)
    const bgmParamNames = ['bgm_music', 'bgmMusic', 'background_music', 'bgm'];
    const bgmParamName = this.settings?.videoModelParameters?.find(p => bgmParamNames.includes(p.name))?.name;
    if (bgmParamName && this.settings?.videoBgmMusic !== null && this.settings?.videoBgmMusic !== undefined) {
      modelParameters[bgmParamName] = this.settings.videoBgmMusic;
    }

    // Loop parameter
    if (hasParameter('loop') && this.settings?.videoLoop !== null && this.settings?.videoLoop !== undefined) {
      modelParameters.loop = this.settings.videoLoop;
    }

    // Camera fixed parameter
    if (hasParameter('camera_fixed') && this.settings?.videoCameraFixed !== null && this.settings?.videoCameraFixed !== undefined) {
      modelParameters.camera_fixed = this.settings.videoCameraFixed;
    }

    // Movement amplitude parameter - normalize to lowercase for enum validation
    if (hasParameter('movement_amplitude') && this.settings?.videoMovementAmplitude) {
      // Convert to lowercase to match backend enum values: "auto", "small", "medium", "large"
      modelParameters.movement_amplitude = String(this.settings.videoMovementAmplitude).toLowerCase();
    }

    // Style parameter - normalize to lowercase for enum validation
    if (hasParameter('style') && this.settings?.videoStyle) {
      // Convert to lowercase to match backend enum values (e.g., "cyberpunk" instead of "Cyberpunk")
      modelParameters.style = String(this.settings.videoStyle).toLowerCase();
    }

    // For image-to-video models
    if (hasParameter('image_url') && this.settings?.videoImageUrl) {
      modelParameters.image_url = this.settings.videoImageUrl;
    }

    // Tail/end image URL parameter (check for variations: tail_image_url, end_image_url)
    const tailImageParamNames = ['tail_image_url', 'end_image_url'];
    const tailImageParamName = this.settings?.videoModelParameters?.find(p => tailImageParamNames.includes(p.name))?.name;
    if (tailImageParamName && this.settings?.videoEndImageUrl) {
      modelParameters[tailImageParamName] = this.settings.videoEndImageUrl;
    }

    // Audio URL parameter (check for variations: audio, custom_audio, audio_url)
    const audioParamNames = ['audio', 'custom_audio', 'audio_url'];
    const audioParamName = this.settings?.videoModelParameters?.find(p => audioParamNames.includes(p.name))?.name;
    if (audioParamName && this.settings?.videoAudioUrl) {
      modelParameters[audioParamName] = this.settings.videoAudioUrl;
    }

    // Delete video parameter - always set to false as per API requirements
    if (hasParameter('delete_video')) {
      modelParameters.delete_video = false;
    }

    // Only add modelParameters if it has at least one property
    if (Object.keys(modelParameters).length > 0) {
      body.modelParameters = modelParameters;
    }

    return body;
  }

  triggerCanvasUpdate() {
    this.setUpdateCanvas(true);
  }

  toBase64 = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
    });
}
